import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brush, Eraser, RotateCcw, Download, X, Palette, Minus, Plus, Save } from 'lucide-react';

const InPaint = ({ isOpen, onClose, imageUrl, onMaskCreated, existingMask = null }) => {
  const canvasRef = useRef(null);
  const maskLayerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [tool, setTool] = useState('brush');
  const [maskDataUrl, setMaskDataUrl] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showCursor, setShowCursor] = useState(false);
  const [hasMask, setHasMask] = useState(false);
  const [savedMaskKey, setSavedMaskKey] = useState(null);

  useEffect(() => {
    if (isOpen && imageUrl) {
      loadImage();
    }
  }, [isOpen, imageUrl]);

  // Genera una chiave unica per salvare la maschera basata sull'URL dell'immagine
  const getMaskStorageKey = (imgUrl) => {
    return `inpaint_mask_${btoa(imgUrl).slice(0, 20)}`;
  };

  const loadImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setOriginalImage(img);
      setupCanvas(img);
      // Carica la maschera salvata dopo aver configurato il canvas
      loadSavedMask();
    };
    img.src = imageUrl;
  };

  const setupCanvas = (img) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Dimensioni più grandi per l'immagine
    const maxWidth = 800;
    const maxHeight = 700;
    const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
    
    canvas.width = img.width * ratio;
    canvas.height = img.height * ratio;
    
    // Disegna l'immagine di sfondo
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    
    // Crea un canvas separato per il layer della maschera
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    maskLayerRef.current = maskCanvas;
    setHasMask(false);
    
    // Imposta la chiave per il salvataggio
    setSavedMaskKey(getMaskStorageKey(imageUrl));
  };

  // Carica la maschera salvata dal localStorage o da existingMask
  const loadSavedMask = () => {
    const maskCanvas = maskLayerRef.current;
    if (!maskCanvas) return;

    let maskToLoad = existingMask;
    
    // Se non c'è una maschera passata come prop, prova a caricare dal localStorage
    if (!maskToLoad && savedMaskKey) {
      maskToLoad = localStorage.getItem(savedMaskKey);
    }

    if (maskToLoad) {
      const img = new Image();
      img.onload = () => {
        const maskCtx = maskCanvas.getContext('2d');
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
        
        // Crea un canvas temporaneo per analizzare la maschera caricata
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(img, 0, 0);
        
        // Ottieni i dati dell'immagine
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const maskData = maskCtx.createImageData(maskCanvas.width, maskCanvas.height);
        
        // Converti: se il pixel è BIANCO nella maschera finale, 
        // rendilo BIANCO nella maschera di lavoro
        for (let i = 0; i < imageData.data.length; i += 4) {
          const r = imageData.data[i];
          const g = imageData.data[i + 1];
          const b = imageData.data[i + 2];
          
          // Se il pixel è bianco (area da modificare nella maschera finale)
          if (r > 128 && g > 128 && b > 128) {
            // Rendilo bianco nella maschera di lavoro
            maskData.data[i] = 255;     // R
            maskData.data[i + 1] = 255; // G
            maskData.data[i + 2] = 255; // B
            maskData.data[i + 3] = 255; // A
          }
          // Altrimenti lascialo trasparente (area da preservare)
        }
        
        // Applica i dati convertiti al canvas della maschera
        maskCtx.putImageData(maskData, 0, 0);
        
        // Ridisegna la visualizzazione
        redrawMaskOverlay();
        setHasMask(true);
      };
      img.src = maskToLoad;
    }
  };

  // Salva la maschera nel localStorage (salva la maschera di lavoro, non quella finale)
  const saveMaskToStorage = () => {
    const maskCanvas = maskLayerRef.current;
    if (savedMaskKey && maskCanvas) {
      const maskUrl = maskCanvas.toDataURL('image/png');
      localStorage.setItem(savedMaskKey, maskUrl);
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setCursorPosition({ x, y });
    
    if (isDrawing) {
      draw(e);
    }
  };

  const handleMouseEnter = () => {
    setShowCursor(true);
  };

  const handleMouseLeave = () => {
    setShowCursor(false);
    setIsDrawing(false);
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const maskCanvas = maskLayerRef.current;
    if (!canvas || !maskCanvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ctx = canvas.getContext('2d');
    const maskCtx = maskCanvas.getContext('2d');
    
    if (tool === 'brush') {
      // Disegna sulla maschera
      maskCtx.globalCompositeOperation = 'source-over';
      maskCtx.fillStyle = 'white';
      maskCtx.beginPath();
      maskCtx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
      maskCtx.fill();
      
      // Invece di disegnare direttamente sul canvas principale,
      // ridisegna tutto per mantenere l'opacità costante
      redrawMaskOverlay();
      
      setHasMask(true);
    } else if (tool === 'eraser') {
      // Cancella dalla maschera
      maskCtx.globalCompositeOperation = 'destination-out';
      maskCtx.beginPath();
      maskCtx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
      maskCtx.fill();
      
      // Ridisegna la maschera visibile
      redrawMaskOverlay();
    }
  };

  const redrawMaskOverlay = () => {
    const canvas = canvasRef.current;
    const maskCanvas = maskLayerRef.current;
    if (!canvas || !maskCanvas || !originalImage) return;
    
    const ctx = canvas.getContext('2d');
    
    // Pulisci e ridisegna l'immagine originale
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
    
    // Crea un canvas temporaneo per la maschera
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    
    // Ridisegna la maschera con trasparenza sul canvas temporaneo
    const maskImageData = maskCanvas.getContext('2d').getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    const overlayImageData = tempCtx.createImageData(canvas.width, canvas.height);
    
    let hasMaskPixels = false;
    for (let i = 0; i < maskImageData.data.length; i += 4) {
      if (maskImageData.data[i + 3] > 0) {
        overlayImageData.data[i] = 255;     // R
        overlayImageData.data[i + 1] = 255; // G
        overlayImageData.data[i + 2] = 255; // B
        overlayImageData.data[i + 3] = 179; // A (70% opacity)
        hasMaskPixels = true;
      }
    }
    
    // Applica la maschera al canvas temporaneo
    tempCtx.putImageData(overlayImageData, 0, 0);
    
    // Sovrapponi la maschera all'immagine principale con globalCompositeOperation
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(tempCanvas, 0, 0);
    
    setHasMask(hasMaskPixels);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const maskCanvas = maskLayerRef.current;
    if (!canvas || !maskCanvas || !originalImage) return;
    
    const ctx = canvas.getContext('2d');
    const maskCtx = maskCanvas.getContext('2d');
    
    // Pulisci entrambi i canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
    
    // Ridisegna solo l'immagine originale
    ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
    setHasMask(false);
    
    // Rimuovi la maschera salvata
    if (savedMaskKey) {
      localStorage.removeItem(savedMaskKey);
    }
  };

  const generateMask = () => {
    const maskCanvas = maskLayerRef.current;
    if (!maskCanvas) return;
    
    // Crea un canvas per la maschera finale con sfondo nero
    const finalMaskCanvas = document.createElement('canvas');
    finalMaskCanvas.width = maskCanvas.width;
    finalMaskCanvas.height = maskCanvas.height;
    const finalCtx = finalMaskCanvas.getContext('2d');
    
    // Riempi tutto di NERO (aree da preservare)
    finalCtx.fillStyle = 'black';
    finalCtx.fillRect(0, 0, finalMaskCanvas.width, finalMaskCanvas.height);
    
    // Disegna le aree BIANCHE dove abbiamo dipinto (aree da modificare)
    const maskImageData = maskCanvas.getContext('2d').getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    const finalImageData = finalCtx.createImageData(finalMaskCanvas.width, finalMaskCanvas.height);
    
    for (let i = 0; i < maskImageData.data.length; i += 4) {
      if (maskImageData.data[i + 3] > 0) { // Se c'è qualcosa dipinto
        // Rendi BIANCO (area da modificare)
        finalImageData.data[i] = 255;     // R
        finalImageData.data[i + 1] = 255; // G
        finalImageData.data[i + 2] = 255; // B
        finalImageData.data[i + 3] = 255; // A
      } else {
        // Mantieni NERO (area da preservare)
        finalImageData.data[i] = 0;       // R
        finalImageData.data[i + 1] = 0;   // G
        finalImageData.data[i + 2] = 0;   // B
        finalImageData.data[i + 3] = 255; // A (opaco)
      }
    }
    
    finalCtx.putImageData(finalImageData, 0, 0);
    
    // Applica un leggero blur per migliorare l'inpainting
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = finalMaskCanvas.width;
    blurCanvas.height = finalMaskCanvas.height;
    const blurCtx = blurCanvas.getContext('2d');
    
    blurCtx.filter = 'blur(2px)';
    blurCtx.drawImage(finalMaskCanvas, 0, 0);
    blurCtx.filter = 'none';
    
    const maskUrl = blurCanvas.toDataURL('image/png');
    setMaskDataUrl(maskUrl);
    
    // NON salvare nel localStorage qui - viene salvata la maschera di lavoro durante il disegno
    
    return maskUrl;
  };

  const handleSaveMask = () => {
    const maskUrl = generateMask();
    if (maskUrl && hasMask) {
      onMaskCreated(maskUrl);
    }
    onClose();
  };

  const downloadMask = () => {
    const maskUrl = generateMask();
    if (maskUrl) {
      const link = document.createElement('a');
      link.download = 'inpaint-mask.png';
      link.href = maskUrl;
      link.click();
    }
  };

  // Gestisce la chiusura automatica come salvataggio
  const handleClose = () => {
    handleSaveMask();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-6xl w-full max-h-[95vh] overflow-auto modern-dark-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                  <Palette className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">InPaint Editor</h3>
                  <p className="text-gray-400 text-sm">Crea una maschera per l'inpainting</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Toolbar orizzontale migliorata */}
            <div className="flex items-center justify-between gap-6 mb-6 p-4 bg-gray-800/50 rounded-xl">
              {/* Tool Selection */}
              <div className="flex gap-3">
                <button
                  onClick={() => setTool('brush')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all ${
                    tool === 'brush'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Brush size={18} />
                  <span className="font-medium">Pennello</span>
                </button>
                <button
                  onClick={() => setTool('eraser')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all ${
                    tool === 'eraser'
                      ? 'bg-red-500 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Eraser size={18} />
                  <span className="font-medium">Gomma</span>
                </button>
              </div>

              {/* Brush Size */}
              <div className="flex items-center gap-4">
                <span className="text-gray-300 text-sm font-medium">Dimensione:</span>
                <button
                  onClick={() => setBrushSize(Math.max(5, brushSize - 5))}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
                >
                  <Minus size={16} />
                </button>
                <span className="text-white font-bold text-lg min-w-[3rem] text-center">{brushSize}px</span>
                <button
                  onClick={() => setBrushSize(Math.min(100, brushSize + 5))}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={clearCanvas}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl text-gray-300 hover:text-white transition-colors"
                >
                  <RotateCcw size={18} />
                  <span className="font-medium">Pulisci</span>
                </button>
                <button
                  onClick={downloadMask}
                  disabled={!hasMask}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-colors ${
                    hasMask
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Download size={18} />
                  <span className="font-medium">Scarica</span>
                </button>
              </div>
            </div>

            {/* Canvas Area */}
            <div className="flex justify-center mb-6">
              <div className="relative border-2 border-gray-700 rounded-xl overflow-hidden bg-gray-800/20">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={handleMouseMove}
                  onMouseUp={stopDrawing}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className="cursor-none"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
                {/* Cursore personalizzato - metodo originale */}
                {showCursor && (
                  <div
                    className="absolute pointer-events-none border-2 border-white rounded-full opacity-70"
                    style={{
                      left: cursorPosition.x - brushSize / 2,
                      top: cursorPosition.y - brushSize / 2,
                      width: brushSize,
                      height: brushSize,
                      borderColor: tool === 'brush' ? '#ffffff' : '#ef4444',
                      backgroundColor: tool === 'brush' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(239, 68, 68, 0.1)'
                    }}
                  />
                )}
              </div>
            </div>

            {/* Status e istruzioni */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {hasMask && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-400 text-sm font-medium">Maschera creata</span>
                  </div>
                )}
                <p className="text-gray-400 text-sm">
                  {tool === 'brush' ? '🎨 Dipingi le aree da modificare' : '🧹 Cancella le aree selezionate'}
                </p>
              </div>
              
              {/* Footer Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl text-gray-300 hover:text-white transition-colors font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveMask}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl text-white font-medium transition-all shadow-lg"
                >
                  Salva Maschera
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InPaint;